import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "src/utils/api";

export const useSignaturesStore = defineStore("signatures_store", {
  state: () => ({
    resolvedMap: {}
  }),
  actions: {
    async insertSignature(data) {
      const result = await Api.Signatures.insertSignature(data);
      if (!result || result.error) {
        throw new Error(result?.error || "Failed to insert signature");
      }
      return result;
    },

    // Resolves a signature by id. When `networkFallback` is true, we try the
    // local RxDB first (cheap, in-memory cached) and fall back to Supabase
    // when the row stub exists locally but the blob (`data` column) hasn't
    // replicated — common for signatures authored by other collaborators.
    async getSignatureById(id, { networkFallback = false } = {}) {
      if (this.resolvedMap[id]?.data) {
        return { data: this.resolvedMap[id] };
      }

      let result = await Api.Signatures.getSignatureById(id, true);

      if (networkFallback && !result?.data?.data) {
        result = await Api.Signatures.getSignatureById(id, false);
      }

      if (result?.data?.data) {
        this.resolvedMap[id] = result.data;
      }
      return result;
    },

    // Soft-delete a signature row. No-ops if the row does not exist
    // (handles backward-compat: old inline signatures never made it to the table).
    //
    // Note: `required_sync: false` is the effective flag here — the pull
    // query filters by `required_sync: true`, so flipping it to false
    // excludes the row from future syncs/reads. `is_deleted: true` is set
    // for parity but RxDB's replication owns that column (deletedField), so
    // it may be overwritten on push; we don't rely on it.
    async softDeleteSignature(id, { networkFallback = false } = {}) {
      if (!id) return null;

      // Bypass cache — query DB directly so we don't act on a stale cached row.
      // Try local first; if not there and fallback is enabled (e.g. the row
      // was authored by another collaborator), look it up on Supabase and
      // perform the soft-delete update there as well.
      let existing = await Api.Signatures.getSignatureById(id, true);
      let foundOnNetwork = false;

      if (networkFallback && !existing?.data) {
        existing = await Api.Signatures.getSignatureById(id, false);
        foundOnNetwork = !!existing?.data;
      }

      if (!existing?.data) return null;

      delete this.resolvedMap[id];

      return await Api.Signatures.updateSignature(
        {
          id,
          is_deleted: true,
          required_sync: false,
          local_updated_at: new Date().toISOString()
        },
        !foundOnNetwork
      );
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSignaturesStore, import.meta.hot));
}
